import { useState, useRef, useCallback } from 'react';
import { DocumentIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Mixpanel } from '../../../services/Mixpanel';
import { uploadPdfToS3 } from '../../Homepage/Utils/GenerationUtils';

/**
 * Form for providing source content via text input, PDF upload, or image upload with drag-and-drop support.
 * @param {string} sourceText - Current text value in the source textarea.
 * @param {Function} setSourceText - Setter for sourceText.
 * @param {Function} onPaste - Paste handler for detecting pasted images.
 * @param {Object} sourceTextareaRef - Ref for the source textarea element.
 * @param {File|null} uploadedFile - The currently uploaded PDF file, if any.
 * @param {Function} setUploadedFile - Setter for uploadedFile.
 * @param {string|null} uploadedFileUrl - The S3 URL of the uploaded PDF, if ready.
 * @param {Function} setUploadedFileUrl - Setter for uploadedFileUrl.
 * @param {boolean} isUploadingFile - Whether a PDF upload is in progress.
 * @param {Function} setIsUploadingFile - Setter for isUploadingFile.
 * @param {string|null} uploadError - Error message from a failed upload, if any.
 * @param {Function} setUploadError - Setter for uploadError.
 * @param {File[]} uploadedImageFiles - Array of uploaded image files.
 * @param {string[]} uploadedImageUrls - Array of S3 URLs for uploaded images.
 * @param {boolean} isUploadingImage - Whether an image upload is in progress.
 * @param {Object} imageInputRef - Ref for the hidden image file input.
 * @param {number} MAX_IMAGES - Maximum number of images allowed.
 * @param {Function} handleImageInputChange - Handler for image file input changes.
 * @param {Function} clearUploadedImage - Removes an uploaded image by index.
 */
export const SourceContentForm = ({
  sourceText,
  setSourceText,
  onPaste,
  sourceTextareaRef,
  // PDF state (lifted to parent so ActionButtons can read it)
  uploadedFile,
  setUploadedFile,
  uploadedFileUrl,
  setUploadedFileUrl,
  isUploadingFile,
  setIsUploadingFile,
  uploadError,
  setUploadError,
  // Image state (lifted to parent so ActionButtons can read it)
  uploadedImageFiles,
  uploadedImageUrls,
  isUploadingImage,
  imageInputRef,
  MAX_IMAGES,
  handleImageInputChange,
  clearUploadedImage,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handlePdfUpload = useCallback(
    async (file) => {
      if (!file) return;

      if (file.size > 50 * 1000 * 1000) {
        setUploadError('Please choose a smaller file: limit is 50MB');
        return;
      }

      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setUploadError('Please upload a PDF file');
        return;
      }

      setUploadedFile(file);
      setIsUploadingFile(true);
      setUploadError(null);

      try {
        const url = await uploadPdfToS3(file);
        setUploadedFileUrl(url);
        Mixpanel.track('Unlimited Source Content PDF Uploaded', {
          file_name: file.name,
          file_size: file.size,
        });
      } catch (err) {
        console.error('Error uploading PDF:', err);
        setUploadError('Failed to upload PDF. Please try again.');
        setUploadedFile(null);
        Mixpanel.track('Unlimited Source Content PDF Upload Failed', {
          error: err.message,
        });
      } finally {
        setIsUploadingFile(false);
      }
    },
    [setUploadedFile, setUploadedFileUrl, setIsUploadingFile, setUploadError]
  );

  const handleFileUpload = useCallback(
    (file) => {
      if (!file) return;
      if (file.name.toLowerCase().endsWith('.pdf')) {
        handlePdfUpload(file);
      } else if (file.type.startsWith('image/')) {
        // Route to image handler via the parent's hook
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        const fakeEvent = { target: { files: dataTransfer.files, value: '' } };
        handleImageInputChange(fakeEvent);
      } else {
        setUploadError('Please upload a PDF or image file');
      }
    },
    [handlePdfUpload, handleImageInputChange, setUploadError]
  );

  const handleFileInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) handleFileUpload(file);
      e.target.value = '';
    },
    [handleFileUpload]
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileUpload(file);
    },
    [handleFileUpload]
  );

  const clearUploadedFile = useCallback(() => {
    setUploadedFile(null);
    setUploadedFileUrl(null);
    setUploadError(null);
  }, [setUploadedFile, setUploadedFileUrl, setUploadError]);

  return (
    <div className='mb-8 space-y-4'>
      {/* Hidden file input for PDF */}
      <input
        ref={fileInputRef}
        type='file'
        accept='.pdf,image/png,image/jpeg,image/jpg,image/gif,image/webp'
        onChange={handleFileInputChange}
        className='hidden'
      />

      {/* Hidden file input for images only */}
      <input
        ref={imageInputRef}
        type='file'
        accept='image/png,image/jpeg,image/jpg,image/gif,image/webp'
        onChange={handleImageInputChange}
        className='hidden'
      />

      {/* Combined textarea for text/links - supports image paste */}
      <textarea
        ref={sourceTextareaRef}
        value={sourceText}
        onChange={(e) => setSourceText(e.target.value)}
        onPaste={onPaste}
        placeholder='Paste text, article links, vocabulary words, video transcripts, or paste/upload an image...'
        className='w-full h-[200px] px-5 py-4 rounded-xl border-2 border-gray-200 bg-white text-gray-800 placeholder:text-gray-400 placeholder:italic focus:border-emerald-500 focus:outline-none focus:ring-0 resize-none transition-colors'
      />

      {/* Or divider */}
      <div className='text-center text-gray-400 text-sm'>Or</div>

      {/* File upload area */}
      {!uploadedFile ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`w-full px-4 py-3 rounded-xl border-2 border-dashed transition-colors cursor-pointer flex items-center justify-center gap-2 ${
            isDragging
              ? 'border-emerald-500 bg-emerald-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
          }`}>
          <DocumentIcon className='w-5 h-5 text-gray-400' />
          <span className='text-gray-600'>Drag file here or </span>
          <span className='text-emerald-600 font-medium'>click to upload</span>
        </div>
      ) : (
        /* Uploaded PDF chip */
        <div className='w-full px-4 py-3 rounded-xl border-2 border-emerald-200 bg-emerald-50 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <DocumentIcon className='w-5 h-5 text-emerald-600' />
            <div className='flex items-center gap-2'>
              <span className='text-gray-800 font-medium truncate max-w-[250px]'>
                {uploadedFile.name}
              </span>
              {isUploadingFile && <span className='text-sm text-emerald-600'>Uploading...</span>}
              {uploadedFileUrl && <span className='text-sm text-emerald-600'>Ready</span>}
            </div>
          </div>
          <button
            onClick={clearUploadedFile}
            disabled={isUploadingFile}
            className='p-1 text-gray-500 hover:text-gray-700 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-50'>
            <XMarkIcon className='w-4 h-4' />
          </button>
        </div>
      )}

      {/* Uploaded image chips */}
      {uploadedImageFiles.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {uploadedImageFiles.map((file, index) => (
            <div
              key={`source-img-${index}`}
              className='inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg max-w-full'>
              <PhotoIcon className='w-4 h-4 text-emerald-600 flex-shrink-0' />
              <span className='text-sm text-gray-700 truncate max-w-[180px]'>{file.name}</span>
              {isUploadingImage &&
              index === uploadedImageFiles.length - 1 &&
              !uploadedImageUrls[index] ? (
                <span className='text-sm text-emerald-600'>Uploading...</span>
              ) : (
                <button
                  onClick={() => clearUploadedImage(index)}
                  className='p-1 text-gray-500 hover:text-gray-700 hover:bg-emerald-100 rounded transition-colors'
                  aria-label={`Remove image ${file.name}`}>
                  <XMarkIcon className='w-4 h-4' />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add image button */}
      {uploadedImageFiles.length < MAX_IMAGES && (
        <button
          onClick={() => imageInputRef.current?.click()}
          className='text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors'>
          + Add image
          {uploadedImageFiles.length > 0 ? ` (${uploadedImageFiles.length}/${MAX_IMAGES})` : ''}
        </button>
      )}

      {/* Upload error */}
      {uploadError && <div className='text-red-600 text-sm'>{uploadError}</div>}
    </div>
  );
};
